import React, { FC, useEffect, useCallback } from "react";
import type { RootState } from "store";
import { useSelector } from "react-redux";
import "styles/pin_detail.scss";
import CONSTANTS from "constants/constants";
import * as Icons from 'constants/icons';

const { MAP } = CONSTANTS.CONFIG.MOLECULES;
const MapView: FC<any> = () => {
  const { searchData, data, showDrawer } = useSelector(
    (state: RootState) => state.search
  );
  const initMap = useCallback(() => {
    const { asrList, latitude, longitude } = data;
    var Maps = (window as any).Microsoft?.Maps;
    if (Maps) {
      var map = new Maps.Map("#asr-map", {
        credentials: process.env.REACT_APP_BING_KEY,
        center: new Maps.Location(
          latitude ? latitude : MAP.POSITION.LATITUDE,
          longitude ? longitude : MAP.POSITION.LONGITUDE
        ),
        bounds: Maps.LocationRect.fromEdges(MAP.VIEW.NORTH, MAP.VIEW.WEST, MAP.VIEW.SOUTH, MAP.VIEW.EAST),
        zoom: MAP.VIEW.ZOOM,
        opacity: 0.7,
        showScalebar: false,        
        showCopyright: false,
        showLogo: false,
        padding: 100,
      });
      if (asrList && asrList.length > 0) {
        let center = new Maps.Location(latitude, longitude);
        let radius = parseInt(searchData.radius.replace(" miles", ""));
        if (showDrawer) {          
          Maps.loadModule("Microsoft.Maps.SpatialMath", function () {
            let circleLoc = Maps.SpatialMath.getRegularPolygon(
              center,
              radius,
              36,
              Maps.SpatialMath.DistanceUnits.Miles
            );
            let poly = new Maps.Polygon(circleLoc, {
              strokeColor: "#116fff",
              fillColor: "rgba(123, 175, 255, 0.21)",
            });
            map.entities.push(poly);
          });
        }
        let infobox = new Maps.Infobox(map.getCenter(), {
          maxHeight: 600,
          maxWidth: 490,          
          visible: false,
        });
        infobox.setMap(map);
        if (showDrawer) {
        let userPin = new Maps.Pushpin(center, { icon: Icons.blueCircle });
        map.entities.push(userPin);
        }
        asrList.forEach((el: any, index: number) => {
          let centerPoint = {
            latitude: el.location.coordinates[0],
            longitude: el.location.coordinates[1],
          };
          const legendIcon = mappingLegendIcons(el.mapLegend);
          let pin = new Maps.Pushpin(centerPoint, legendIcon);          
          const emailId = el.email ? `<a href=mailto:${el.email}>${el.email}</a>` : 'N/A' 
          pin.metadata = {
            title: el.partner_name,
            description:
              `<div class='pin-detail'>
                <section>
                  <div class='name'>Partner:</div>
                  <div class='value'>${el.account_number ?? 'N/A'} <span id="copy_${index}" class='copy-section'><span class='copied_text'>Copied</span> ${el.account_number ? `<img onclick="copyToClipBoard(${el.account_number}, ${index})" class='image_style' src=${Icons.copy} alt='Copy' />` : ''}</span></div>
                </section>
                <section>
                  <div class='name'>NPS:</div>
                  <div class='value'>${el.nps ?? 'N/A'}</div>
                </section>
                <section>
                  <div class='name'>Phone:</div>
                  <div class='value'>${el.phone ?? 'N/A'}</div>
                </section>
                <section>
                  <div class='name'>Email:</div>
                  <div class='value'>${emailId}</div>
                </section>
                <section>
                  <div class='name'>Address:</div>
                  <div class='value'>${el.address} ${el.city} ${el.state} ${el.country} ${el.zip_postal} </div>
                </section>
                <section>
                  <div class='name'>City:</div>
                  <div class='value'>${el.city ?? 'N/A'} </div>
                </section>
                <section>
                  <div class='name'>Expertise:</div>
                  <div class='value'>${el.SkillId ?? 'N/A'} </div>
                </section>
                <div>
                  <h3>ASR Latest Comments</h3>
                  <div class='comments'>${el.comments ?? 'N/A'}</div>
                </div>
              </div>`
          };

          Maps.Events.addHandler(pin, "click", (e: React.ChangeEvent<any>) => {
            if (e.target.metadata) {
              infobox.setOptions({
                location: e.target.getLocation(),
                title: e.target.metadata.title,
                description: e.target.metadata.description,
                visible: true,
                autoPan: true,
                autoAlignment: true,
              });
            }
          });
          map.entities.push(pin);
        });
        if (showDrawer) {
          const zoomLevel = getZoomLevel(
            radius,
            map.getCenter().latitude,
            350,
            250
          );
          map.setView({
            center: center,
            zoom: zoomLevel,
            padding: 100,
            strokeOpacity: 0.6,
          });
        }
      }
    }
  }, [data, searchData.radius]);
  const mappingLegendIcons = (mapLegendName: string) => {
    const mapLegends = ["F","IS", "KK"];
    return {
      icon:
        mapLegends.includes(mapLegendName) &&
        require(`assets/icons/legend-${mapLegendName?.toLowerCase()}.svg`),
    };
  };

  const getZoomLevel = (
    radius: any,
    latitude: any,
    heightOfMapInPixels: any,
    widthOfMapInPixels: any
  ) => {
    const range = radius * 1.6 * 1000;
    const limitBoundPixels = Math.min(heightOfMapInPixels, widthOfMapInPixels);
    const zoom = Math.floor(
      Math.log(
        (156543.03392 * Math.cos((latitude * Math.PI) / 180)) /
          (range / limitBoundPixels)
      ) / Math.log(2)
    );
    return zoom;
  };

  (window as any).initMap = initMap;

  useEffect(() => {
    if (!document.querySelector('[data-bing="true"]')) {
      const scriptTag = document.createElement("script");
      scriptTag.src = process.env.REACT_APP_BING_SDK_URL + "?callback=initMap";
      scriptTag.async = true;
      scriptTag.dataset.bing = "true";
      document.body.appendChild(scriptTag);
    }
    (window as any).copyToClipBoard = (str: string, index: number) => {
      let copySectionEle = document.getElementById(`copy_${index}`)
      copySectionEle?.classList.add("copied")
      navigator.clipboard.writeText(str).then(
        () => {
          setTimeout(() => {
            copySectionEle?.classList.remove("copied")
          }, 2000);
        },
        () => copySectionEle?.classList.remove("copied")
      );
    }
  }, []);

  useEffect(() => {
    initMap();
  }, [data.asrList]);

  return (
    <div className="asr-map">
      <div
        id="asr-map"
        style={{ position: "relative", width: "100%", height: "100%" }}
      ></div>
    </div>
  );
};
export default MapView;
